import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Typography, IconButton } from '@material-ui/core'
import IconBack from '@material-ui/icons/ArrowBack'
import { injectIntl, FormattedMessage } from 'react-intl'
import {
  userFetchFormData,
  userChange,
  userClearStateForm,
} from 'reducers/user/reducer'
import { siteFetchList } from 'reducers/site/reducer'
import { fetchUserGroupList } from 'reducers/userGroup/reducer'
import UserForm from 'components/user/UserForm'
import withPromise from 'helpers/withPromise'
import messages from 'components/user/translate'
import NoticeHelper from 'helpers/notice'
import { showNotice } from 'reducers/notice'
import { REACT_INTL_PROP_TYPE } from 'constants/propTypes'
import Psfs from 'lib/psfs/Psfs'
import Loader from 'components/Loading'

function UserItem({
  applications,
  formData,
  formDataLoading,
  siteList,
  userGroupList,
  roleLevel,
  userClearStateForm,
  userChange,
  showNotice,
  siteFetchList,
  fetchUserGroupList,
  userFetchFormData,
  history: { goBack },
  intl: { formatMessage },
  match: { params },
}) {
  const [redirect, setRedirect] = useState(false)
  const { user, roles, licensees } = formData

  const getEntityId = () => {
    const { id } = params

    return id !== 'create' ? id : false
  }

  const fetchSites = (licensee = null) => {
    const psfs = new Psfs()
    psfs
      .addFields(['id', 'name'])
      .addFilter('active', 1)
      .addFilter('visibleInUi', 1)

    siteFetchList({ psfs, licensee })
  }

  const fetchUserGroups = (site, licensee) => {
    const psfs = new Psfs()
    psfs.addFields(['id', 'name'])

    fetchUserGroupList({ psfs, licensee })
  }

  useEffect(() => {
    const entityId = getEntityId()
    userFetchFormData({
      entityId,
      roleLevel,
    })

    fetchSites()

    return () => userClearStateForm()
  }, [])

  useEffect(() => {
    if (user && user.site) {
      fetchUserGroups(user.site.id)
    }
  }, [user])

  useEffect(() => {
    if (redirect) {
      goBack()
    }
  }, [redirect, goBack])

  const onSubmit = ({
    roles,
    name,
    email,
    username,
    site,
    licensee,
    userGroups,
    locale,
    canCreateJobs,
    canDeleteJobs,
    canDeleteJobSeries,
    canSeeDeletedJobs,
    canRecoverDeletedJobs,
    canManageWorkflows,
    canReopenJobs,
    canSetSensorsLimits,
    enabled,
  }) => {
    const data = {
      email,
      name,
      username,
      site,
      locale,
      userGroups,
      roles,
      canCreateJobs: !!canCreateJobs,
      canDeleteJobs: !!canDeleteJobs,
      canDeleteJobSeries: !!canDeleteJobSeries,
      canManageWorkflows: !!canManageWorkflows,
      canReopenJobs: !!canReopenJobs,
      canSetSensorsLimits: !!canSetSensorsLimits,
      canSeeDeletedJobs: !!canSeeDeletedJobs,
      canRecoverDeletedJobs: !!canRecoverDeletedJobs,
      enabled: !!enabled,
    }

    if (licensee) {
      data.licensee = licensee
    }

    if (!username && !email) {
      showNotice({
        notice: NoticeHelper.createWarningObj(
          formatMessage(messages.validationMessage),
        ),
      })

      return
    }

    if (email === '') {
      delete data.email
    }

    const entityId = getEntityId()

    return withPromise(userChange, {
      entityId,
      values: data,
    }).then(errors => {
      if (errors) {
        return Promise.resolve(errors)
      }

      setRedirect(true)
    })
  }

  const handleLicenseeChange = licensee => {
    fetchSites(licensee)
  }

  const handleSiteChange = (site, licensee) => {
    fetchUserGroups(site, licensee)
  }

  if (formDataLoading) {
    return <Loader pastDelay />
  }

  return (
    <div className="container-fluid no-breadcrumb">
      <Typography variant="h5" className="headline">
        <IconButton size="medium" className="btn-back" onClick={goBack}>
          <IconBack fontSize="small" />
        </IconButton>
        <FormattedMessage {...messages.userInfo} />
      </Typography>
      <UserForm
        user={user}
        roles={roles}
        licensees={licensees}
        siteList={siteList.toJS()}
        userGroupList={userGroupList.toJS()}
        onSubmit={onSubmit}
        applications={applications}
        roleLevel={roleLevel}
        handleLicenseeChange={handleLicenseeChange}
        handleSiteChange={handleSiteChange}
      />
    </div>
  )
}

UserItem.propTypes = {
  intl: REACT_INTL_PROP_TYPE.isRequired,
  applications: PropTypes.array.isRequired,
  formData: PropTypes.object.isRequired,
  formDataLoading: PropTypes.bool.isRequired,
  siteList: PropTypes.object.isRequired,
  userGroupList: PropTypes.object.isRequired,
  userFetchFormData: PropTypes.func.isRequired,
  roleLevel: PropTypes.number.isRequired,
  userChange: PropTypes.func.isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  userClearStateForm: PropTypes.func,
  siteFetchList: PropTypes.func.isRequired,
  fetchUserGroupList: PropTypes.func.isRequired,
  showNotice: PropTypes.func,
}

UserItem.defaultProps = {
  userClearStateForm: () => {},
  showNotice: () => {},
}

export default connect(
  state => ({
    formData: state.getIn(['user', 'formData']).toJS(),
    formDataLoading: state.getIn(['user', 'formDataLoading']),
    siteList: state.getIn(['site', 'listData']),
    userGroupList: state.getIn(['userGroup', 'userGroupList']),
    roleLevel: state.getIn(['profile', 'roleLevel']),
  }),
  {
    userFetchFormData,
    userChange,
    userClearStateForm,
    siteFetchList,
    fetchUserGroupList,
    showNotice,
  },
)(injectIntl(UserItem))
